/**
 * Supabase API Integration
 *
 * Handles all interactions with the Supabase database containing:
 * - rss_content table (source content from feeds)
 * - blog_posts table (AI-generated articles ready to publish)
 *
 * This replaces the previous Airtable integration.
 */

import { createClient } from "@supabase/supabase-js";

// Configure Supabase
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  console.warn("Supabase configuration missing. Please check your .env file.");
}

// Initialize Supabase client
const supabase =
  SUPABASE_URL && SUPABASE_ANON_KEY ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY) : null;

/**
 * Transform RSS Content record from Supabase to RSSItem object
 * Maintains compatibility with the existing frontend interface
 * @param {Object} record - Supabase record from rss_content table
 * @returns {import('../types').RSSItem}
 */
const transformRSSRecord = (record) => {
  return {
    id: record.ID || record.id,
    sourceHeadline: record.sourceHeadline || "",
    sourceSummary: record.sourceSummary || "",
    sourceURL: record.sourceURL || "",
    sourceChannel: record.sourceChannel || "",
    status: record.Status || "New",
    date: record.Date || record.created_at,
    created_at: record.Date || record.created_at,
  };
};

/**
 * Transform Blog Posts record from Supabase to Post object
 * @param {Object} record - Supabase record from blog_posts table
 * @returns {import('../types').Post}
 */
const transformBlogPostRecord = (record) => {
  // Extract data from related RSS content if available
  const rssContent = record.rss_content;
  const rssDate = rssContent?.Date;
  const rssBlogUrl = rssContent?.blog_url;
  const rssLinkedInUrl = rssContent?.linkedin_url;
  const rssXUrl = rssContent?.x_url;
  const rssThreadsUrl = rssContent?.threads_url;

  // Always use Date column from RSS content as it contains actual posted dates from migration
  // Fall back to record.Date (unlikely) or created_at (migration timestamp) only if RSS date unavailable
  const displayDate = rssDate || record.Date || record.created_at;

  return {
    id: record.ID || record.id,
    title: record.Title || "",
    abstract: record.Abstract || "",
    content: record.Content || "",
    status: record.Status || "Pending Approval",
    linkedinCopy: record["LinkedIn Copy"] || "",
    xCopy: record["X Copy"] || "",
    threadsCopy: record["Threads Copy"] || "",
    instagramCopy: record["Instagram Copy"] || "",
    article_link: record["Article Link"] || "",
    banner_image: record.Attachments?.[0]?.url || record["Banner Image"]?.[0]?.url || "",
    rssContentId: record["RSS Content"] || record.rss_content_id || null,
    sourceHeadline: record.sourceHeadline || record["sourceHeadline (from RSS Content)"] || "",
    sourceSummary: record.sourceSummary || record["sourceSummary (from RSS Content)"] || "",
    sourceURL: record.sourceURL || record["sourceURL (from RSS Content)"] || "",
    sourceChannel: record.sourceChannel || record["sourceChannel (from RSS Content)"] || "",
    date: displayDate,
    created_at: record.created_at,
    published_at: rssDate || record.published_at,
    blog_url: rssBlogUrl || record.blog_url || record["blog_url (from RSS Content)"] || "",
    linkedin_post_url:
      rssLinkedInUrl ||
      record.linkedin_post_url ||
      record.linkedin_url ||
      record["linkedin_url (from RSS Content)"] ||
      "",
    x_post_url:
      rssXUrl || record.x_post_url || record.x_url || record["x_url (from RSS Content)"] || "",
    threads_post_url:
      rssThreadsUrl ||
      record.threads_post_url ||
      record.threads_url ||
      record["threads_url (from RSS Content)"] ||
      "",
  };
};

// Legacy transform for backward compatibility
// eslint-disable-next-line no-unused-vars
const transformRecord = transformBlogPostRecord;

/**
 * Fetch RSS Content items by status
 * @param {string} status - Status to filter by ('New', 'Selected', etc.)
 * @returns {Promise<import('../types').RSSItem[]>}
 */
export const fetchRSSContent = async (status = "New") => {
  if (!supabase) {
    throw new Error("Supabase not configured. Please check environment variables.");
  }

  try {
    const { data, error } = await supabase
      .from("rss_content")
      .select("*")
      .eq("Status", status)
      .order("Date", { ascending: false, nullsFirst: false })
      .limit(100);

    if (error) throw error;

    return (data || []).map(transformRSSRecord);
  } catch (error) {
    console.error("Error fetching RSS content from Supabase:", error);
    throw new Error(`Failed to fetch RSS content: ${error.message}`);
  }
};

/**
 * Fetch RSS Content items by multiple statuses
 * @param {string[]} statuses - Array of statuses to filter by ['New', 'Processing']
 * @returns {Promise<import('../types').RSSItem[]>}
 */
export const fetchRSSContentMultipleStatuses = async (statuses = ["New"]) => {
  if (!supabase) {
    throw new Error("Supabase not configured. Please check environment variables.");
  }

  try {
    const { data, error } = await supabase
      .from("rss_content")
      .select("*")
      .in("Status", statuses)
      .order("Date", { ascending: false, nullsFirst: false })
      .limit(100);

    if (error) throw error;

    return (data || []).map(transformRSSRecord);
  } catch (error) {
    console.error("Error fetching RSS content from Supabase:", error);
    throw new Error(`Failed to fetch RSS content: ${error.message}`);
  }
};

/**
 * Fetch blog posts by status from blog_posts table
 * @param {string} status - Status to filter by ('Good to go', 'Posted', 'Rejected')
 * @returns {Promise<import('../types').Post[]>}
 */
export const fetchBlogPosts = async (status) => {
  if (!supabase) {
    throw new Error("Supabase not configured. Please check environment variables.");
  }

  try {
    // Fetch blog posts with optional related RSS content data
    const { data, error } = await supabase
      .from("blog_posts")
      .select(
        `
        *,
        rss_content:rss_content_id (
          Date,
          blog_url,
          linkedin_url,
          x_url,
          threads_url
        )
      `
      )
      .eq("Status", status)
      .order("created_at", { ascending: false, nullsFirst: false })
      .limit(100);

    if (error) throw error;

    // Fetch all RSS content with Posted status to match with blog posts
    const { data: rssData } = await supabase.from("rss_content").select("*").eq("Status", "Posted");

    // Create maps for matching: by ID, by title
    const rssById = new Map();
    const rssByTitle = new Map();
    if (rssData) {
      rssData.forEach((rss) => {
        // Map by ID
        rssById.set(rss.ID, rss);

        // Map by title (lowercase, trimmed)
        const title = rss.sourceHeadline?.toLowerCase().trim();
        if (title) {
          rssByTitle.set(title, rss);
        }
      });
    }

    return (data || []).map((record) => {
      // If no RSS content joined, try multiple ways to find it
      if (!record.rss_content) {
        // Try 1: Use 'RSS Content' field (Airtable-style field name)
        const rssContentId = record["RSS Content"] || record["RSS Content ID"];
        if (rssContentId && rssById.has(rssContentId)) {
          record.rss_content = rssById.get(rssContentId);
        }
        // Try 2: Match by title if still not found
        else {
          const postTitle = record.Title?.toLowerCase().trim();
          if (postTitle && rssByTitle.has(postTitle)) {
            record.rss_content = rssByTitle.get(postTitle);
          }
        }
      }
      return transformBlogPostRecord(record);
    });
  } catch (error) {
    console.error("Error fetching blog posts from Supabase:", error);
    throw new Error(`Failed to fetch blog posts: ${error.message}`);
  }
};

/**
 * Fetch posts - fetches from blog_posts table
 * Used for Drafts, Posted, and Rejected tabs
 * @param {string} status - Status to filter by ('Good to go', 'Posted', 'Rejected')
 * @returns {Promise<import('../types').Post[]>}
 */
export const fetchPosts = async (status) => {
  return fetchBlogPosts(status);
};

/**
 * Update RSS Content item status
 * @param {string} id - Record ID
 * @param {string} status - New status
 * @returns {Promise<import('../types').RSSItem>}
 */
export const updateRSSContentStatus = async (id, status) => {
  if (!supabase) {
    throw new Error("Supabase not configured. Please check environment variables.");
  }

  try {
    const { data, error } = await supabase
      .from("rss_content")
      .update({ Status: status })
      .eq("ID", id)
      .select()
      .single();

    if (error) throw error;

    return transformRSSRecord(data);
  } catch (error) {
    console.error("Error updating RSS content status:", error);
    throw new Error(`Failed to update RSS content: ${error.message}`);
  }
};

/**
 * Update blog post status in blog_posts table
 * @param {string} id - Record ID
 * @param {string} status - New status
 * @param {Object} additionalFields - Additional fields to update
 * @returns {Promise<import('../types').Post>}
 */
export const updateBlogPostStatus = async (id, status, additionalFields = {}) => {
  if (!supabase) {
    throw new Error("Supabase not configured. Please check environment variables.");
  }

  try {
    const { data, error } = await supabase
      .from("blog_posts")
      .update({ Status: status, ...additionalFields })
      .eq("ID", id)
      .select()
      .single();

    if (error) throw error;

    return transformBlogPostRecord(data);
  } catch (error) {
    console.error("Error updating blog post status:", error);
    throw new Error(`Failed to update blog post: ${error.message}`);
  }
};

// Legacy function for backward compatibility
export const updatePostStatus = updateBlogPostStatus;

/**
 * Update blog post fields (without changing status)
 * @param {string} id - Record ID
 * @param {Object} fields - Fields to update (using frontend naming)
 * @returns {Promise<import('../types').Post>}
 */
export const updateBlogPost = async (id, fields) => {
  if (!supabase) {
    throw new Error("Supabase not configured. Please check environment variables.");
  }

  try {
    // Map frontend field names to database column names
    const dbFields = {};

    if (fields.content !== undefined) dbFields.Content = fields.content;
    if (fields.linkedinCopy !== undefined) dbFields["LinkedIn Copy"] = fields.linkedinCopy;
    if (fields.xCopy !== undefined) dbFields["X Copy"] = fields.xCopy;
    if (fields.threadsCopy !== undefined) dbFields["Threads Copy"] = fields.threadsCopy;
    if (fields.title !== undefined) dbFields.Title = fields.title;
    if (fields.abstract !== undefined) dbFields.Abstract = fields.abstract;

    // If no mapping needed, use fields directly (for direct db column names)
    const fieldsToUpdate = Object.keys(dbFields).length > 0 ? dbFields : fields;

    const { data, error } = await supabase
      .from("blog_posts")
      .update(fieldsToUpdate)
      .eq("ID", id)
      .select()
      .single();

    if (error) throw error;

    return transformBlogPostRecord(data);
  } catch (error) {
    console.error("Error updating blog post:", error);
    throw new Error(`Failed to update blog post: ${error.message}`);
  }
};

/**
 * Get a single blog post by ID from blog_posts table
 * @param {string} id - Record ID
 * @returns {Promise<import('../types').Post>}
 */
export const getBlogPostById = async (id) => {
  if (!supabase) {
    throw new Error("Supabase not configured. Please check environment variables.");
  }

  try {
    const { data, error } = await supabase
      .from("blog_posts")
      .select(
        `
        *,
        rss_content:rss_content_id (
          Date,
          blog_url,
          linkedin_url,
          x_url,
          threads_url
        )
      `
      )
      .eq("ID", id)
      .single();

    if (error) throw error;

    return transformBlogPostRecord(data);
  } catch (error) {
    console.error("Error fetching blog post by ID:", error);
    throw new Error(`Failed to fetch blog post: ${error.message}`);
  }
};

// Legacy function for backward compatibility
export const getPostById = getBlogPostById;

/**
 * Fetch all blog posts (for calendar view or admin)
 * @returns {Promise<import('../types').Post[]>}
 */
export const fetchAllBlogPosts = async () => {
  if (!supabase) {
    throw new Error("Supabase not configured. Please check environment variables.");
  }

  try {
    const { data, error } = await supabase
      .from("blog_posts")
      .select(
        `
        *,
        rss_content:rss_content_id (
          Date,
          blog_url,
          linkedin_url,
          x_url,
          threads_url
        )
      `
      )
      .order("created_at", { ascending: false, nullsFirst: false })
      .limit(200);

    if (error) throw error;

    return (data || []).map(transformBlogPostRecord);
  } catch (error) {
    console.error("Error fetching all blog posts:", error);
    throw new Error(`Failed to fetch blog posts: ${error.message}`);
  }
};

// Legacy function for backward compatibility
export const fetchAllPosts = fetchAllBlogPosts;

/**
 * Check if a URL already exists in the rss_content table
 * @param {string} url - The URL to check
 * @returns {Promise<{exists: boolean, record?: any}>}
 */
export const checkUrlExists = async (url) => {
  if (!supabase) {
    throw new Error("Supabase client not initialized");
  }

  if (!url || typeof url !== "string") {
    throw new Error("Invalid URL provided");
  }

  try {
    const { data, error } = await supabase
      .from("rss_content")
      .select("*")
      .eq("sourceURL", url.trim())
      .limit(1);

    if (error) throw error;

    return {
      exists: data && data.length > 0,
      record: data && data.length > 0 ? data[0] : null,
    };
  } catch (error) {
    console.error("Error checking URL existence:", error);
    throw new Error(`Failed to check URL: ${error.message}`);
  }
};

/**
 * Get the Supabase client for advanced operations
 * @returns {import('@supabase/supabase-js').SupabaseClient | null}
 */
export const getSupabaseClient = () => supabase;

export default supabase;
